import type { NativeVideoCodec } from '../../native-video/NativeVideoProtocol';
import type { WcConfig } from '../protocol/WebCodecsTypes';
import { WebCodecsWire } from '../protocol/WebCodecsWire';
import type { WcDecoderPlatform, WcWorkerPlatform } from './WebCodecsClientTypes';

/** Checks recognized configuration fields, never infers hardware use or successful decoding. */
export class WebCodecsCapabilities {
  constructor(private readonly platform: Pick<WcDecoderPlatform, 'isConfigSupported'>,
    private readonly timers: Pick<WcWorkerPlatform, 'setTimer' | 'clearTimer'> = {
      setTimer: (callback, delay) => globalThis.setTimeout(callback, delay) as unknown as number,
      clearTimer: id => globalThis.clearTimeout(id),
    }) {}

  private async probe(config: VideoDecoderConfig): Promise<VideoDecoderSupport> {
    let timer: number | undefined;
    try {
      return await Promise.race([this.platform.isConfigSupported(config), new Promise<VideoDecoderSupport>((_, reject) => {
        timer = this.timers.setTimer(() => reject(new Error('Decoder support probe timed out')), 1500);
      })]);
    } finally { if (timer !== undefined) this.timers.clearTimer(timer); }
  }

  static decoderConfig(config: WcConfig): VideoDecoderConfig {
    WebCodecsWire.EncodeControl({ v: 1, type: 'configure', session: 'native-player-probe', config });
    const prefixes = { h264: /^avc1\.[0-9a-f]{6}$/i, hevc: /^hev1\./, av1: /^av01\./ };
    if (!prefixes[config.codec].test(config.codecString)) throw new Error('Codec family does not match configuration');
    return { codec: config.codecString, codedWidth: config.width, codedHeight: config.height,
      colorSpace: { primaries: config.primaries, transfer: config.transfer, matrix: config.matrix, fullRange: config.fullRange },
      hardwareAcceleration: 'prefer-hardware', optimizeForLatency: true };
  }

  async exact(config: WcConfig): Promise<VideoDecoderConfig | null> {
    try {
      const requested = WebCodecsCapabilities.decoderConfig(config);
      const result = await this.probe(requested), recognized = result.config;
      // Omitted acceleration/latency is not a software choice. Safari often drops those echoes
      // while still decoding the requested codec in hardware. An explicit downgrade is a refusal.
      if (!result.supported || !recognized || recognized.codec !== requested.codec ||
        recognized.codedWidth !== requested.codedWidth || recognized.codedHeight !== requested.codedHeight ||
        recognized.optimizeForLatency === false || recognized.hardwareAcceleration === 'no-preference' ||
        recognized.hardwareAcceleration === 'prefer-software') return null;
      // Safari copies only the fields it recognizes. A missing color space is not a mismatch.
      const echoed = recognized.colorSpace;
      if (echoed) {
        for (const field of ['primaries', 'transfer', 'matrix', 'fullRange'] as const)
          if (echoed[field] !== undefined && echoed[field] !== requested.colorSpace?.[field]) return null;
      }
      return requested;
    } catch { return null; }
  }

  /** Coarse eligibility only; the worker subsequently probes every actual encoded configuration. */
  async families(platformReady: boolean): Promise<NativeVideoCodec[]> {
    if (!platformReady) return [];
    const strings = { h264: 'avc1.42C01F', hevc: 'hev1.1.6.L93.B0', av1: 'av01.0.08M.08.0.110.01.01.01.0' };
    const supported: NativeVideoCodec[] = [];
    for (const codec of ['h264', 'hevc', 'av1'] as const) {
      const config: WcConfig = { epoch: 1, codec, codecString: strings[codec], format: codec === 'av1' ? 'av1-obu' : 'annexb',
        width: 1280, height: 720, fps: 60, bitDepth: 8, primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
      if (await this.exact(config)) supported.push(codec);
    }
    return supported;
  }
}
